import { isGridless } from '../grid/grid.js';
import { heightSquares, squares3D } from '../grid/measure.js';
import { footprintSquares } from '../state/fog.js';
import { elevation } from '../state/floors.js';
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const isText = (v, most = 60) => typeof v === 'string' && v.trim().length > 0 && v.length <= most;
const only = (o, keys) => Object.keys(o).every((k) => keys.includes(k));
const isCount = (v) => typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 1000;
function parseSize(v) {
    if (!isObj(v) || !only(v, ['size', 'squares', 'reach']) || !isText(v.size) || !isCount(v.squares) || !isCount(v.reach))
        return null;
    return { size: v.size.trim().toLowerCase(), squares: v.squares, reach: v.reach };
}
function parseWeapon(v) {
    if (!isObj(v) || !only(v, ['names', 'times', 'reach', 'adjacent', 'no_threat']))
        return null;
    if (!Array.isArray(v.names) || !v.names.length || v.names.length > 6 || !v.names.every((n) => isText(n)))
        return null;
    if ((v.times === undefined) === (v.reach === undefined))
        return null;
    if (v.times !== undefined && !(typeof v.times === 'number' && Number.isInteger(v.times) && v.times >= 2 && v.times <= 10))
        return null;
    if (v.reach !== undefined && !isCount(v.reach))
        return null;
    for (const k of ['adjacent', 'no_threat'])
        if (v[k] !== undefined && v[k] !== true)
            return null;
    return {
        names: v.names.map(wordsOf), ...(v.times !== undefined ? { times: v.times } : {}), ...(v.reach !== undefined ? { reach: v.reach } : {}),
        ...(v.adjacent ? { adjacent: true } : {}), ...(v.no_threat ? { noThreat: true } : {}),
    };
}
const isPenalty = (v) => typeof v === 'number' && Number.isInteger(v) && v < 0 && v >= -50;
const isTimes = (v) => typeof v === 'number' && Number.isFinite(v) && v > 0 && v <= 10;
function parseRangeFeat(v) {
    if (!isObj(v) || !only(v, ['feat', 'times', 'thrown_times', 'penalty']) || !isText(v.feat) || Object.keys(v).length < 2)
        return null;
    if ((v.times !== undefined && !isTimes(v.times)) || (v.thrown_times !== undefined && !isTimes(v.thrown_times)) || (v.penalty !== undefined && !isPenalty(v.penalty)))
        return null;
    return { feat: v.feat.trim(), ...(v.times !== undefined ? { times: v.times } : {}), ...(v.thrown_times !== undefined ? { thrownTimes: v.thrown_times } : {}), ...(v.penalty !== undefined ? { penalty: v.penalty } : {}) };
}
function parseRange(v) {
    if (!isObj(v) || !only(v, ['family', 'field', 'thrown_field', 'most', 'most_thrown', 'penalty', 'label', 'feats']))
        return null;
    if (!isText(v.family) || !isText(v.field) || !isText(v.thrown_field) || !isText(v.label) || !isCount(v.most) || !isCount(v.most_thrown))
        return null;
    if (!isPenalty(v.penalty))
        return null;
    const feats = (v.feats === undefined ? [] : Array.isArray(v.feats) ? v.feats : [null]).map(parseRangeFeat);
    if (!feats.every((f) => f !== null))
        return null;
    return { family: v.family, field: v.field, thrownField: v.thrown_field, most: v.most, mostThrown: v.most_thrown, penalty: v.penalty, label: v.label.trim(), feats };
}
function parseIntoMelee(v) {
    if (!isObj(v) || !only(v, ['penalty', 'label', 'clear', 'unless']) || !isPenalty(v.penalty) || !isText(v.label) || !isCount(v.clear))
        return null;
    const unless = v.unless === undefined ? [] : v.unless;
    if (!Array.isArray(unless) || !unless.every((f) => isText(f)))
        return null;
    return { penalty: v.penalty, label: v.label.trim(), clear: v.clear, unless: unless.map((f) => f.trim()) };
}
const isHow = (v) => v === 'melee' || v === 'ranged';
/** `features.combat.miss_chance`, strictly: null when any part is malformed, so no feat changes a miss chance. */
export function parseMissChance(v) {
    if (!isObj(v) || !only(v, ['reroll', 'ignore']))
        return null;
    const reroll = [];
    const ignore = [];
    for (const r of v.reroll === undefined ? [] : Array.isArray(v.reroll) ? v.reroll : [null]) {
        if (!isObj(r) || !only(r, ['feat', 'only']) || !isText(r.feat) || !isHow(r.only))
            return null;
        reroll.push({ feat: r.feat.trim(), only: r.only });
    }
    for (const r of v.ignore === undefined ? [] : Array.isArray(v.ignore) ? v.ignore : [null]) {
        if (!isObj(r) || !only(r, ['feat', 'only', 'below']) || !isText(r.feat) || !isHow(r.only) || !isCount(r.below))
            return null;
        ignore.push({ feat: r.feat.trim(), only: r.only, below: r.below });
    }
    return { reroll, ignore };
}
/** `features.combat.reach`, strictly: null when any part is malformed, so the table measures nothing. */
export function parseWeaponReach(v) {
    if (!isObj(v) || !only(v, ['stat', 'size_stat', 'sizes', 'weapons', 'adds', 'readied', 'range', 'into_melee']))
        return null;
    if (!isText(v.stat) || !isText(v.size_stat) || !Array.isArray(v.sizes) || !v.sizes.length || v.sizes.length > 20)
        return null;
    if (!Array.isArray(v.weapons) || v.weapons.length > 60)
        return null;
    const sizes = v.sizes.map(parseSize);
    const weapons = v.weapons.map(parseWeapon);
    if (!sizes.every((s) => s !== null) || !weapons.every((w) => w !== null))
        return null;
    const adds = [];
    for (const a of v.adds === undefined ? [] : Array.isArray(v.adds) ? v.adds : [null]) {
        if (!isObj(a) || !only(a, ['effect', 'adds']) || !isText(a.effect) || !isCount(a.adds))
            return null;
        adds.push({ effect: a.effect.trim(), adds: a.adds });
    }
    const readied = v.readied === undefined ? [] : v.readied;
    if (!Array.isArray(readied) || !readied.every((f) => isText(f)))
        return null;
    const range = v.range === undefined ? undefined : parseRange(v.range);
    if (range === null)
        return null;
    const intoMelee = v.into_melee === undefined ? undefined : parseIntoMelee(v.into_melee);
    if (intoMelee === null)
        return null;
    return { stat: v.stat, sizeStat: v.size_stat, sizes, weapons, adds, readied: readied.map((f) => f.trim()), ...(range ? { range } : {}), ...(intoMelee ? { intoMelee } : {}) };
}
/** Lower case, words one space apart, punctuation gone: "Chain, spiked" reads "chain spiked". */
function wordsOf(s) { return s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim(); }
const hasWords = (text, words) => ` ${text} `.includes(` ${words} `);
const singular = (w) => w.replace(/s$/, '');
/** A stat block's reach, "10 ft. (15 ft. with bite and gore)" or a plain number: null when it holds no number. */
export function parseStatReach(text) {
    const first = /\d+/.exec(text);
    if (!first)
        return null;
    const out = { natural: Number(first[0]), with: [] };
    for (const m of text.matchAll(/(\d+)[^\d()]*\bwith\s+([^);]+)/gi)) {
        out.with.push({ reach: Number(m[1]), names: m[2].split(/,|\band\b/).map((n) => singular(wordsOf(n))).filter(Boolean) });
    }
    return out;
}
/** A creature's reach by its size's word, else by how many squares wide its token stands. */
export function reachBySize(rule, size, footprint) {
    const named = size ? rule.sizes.find((s) => hasWords(wordsOf(size), s.size)) : undefined;
    if (named)
        return { natural: named.reach, with: [] };
    const across = Math.max(footprint.w, footprint.h);
    let found = rule.sizes[0];
    for (const s of rule.sizes)
        if (s.squares <= across + 1e-9)
            found = s;
    return { natural: found.reach, with: [] };
}
/** The band of the attack of this name, from a creature of this natural reach. */
export function reachBand(rule, nat, attack) {
    const name = wordsOf(attack);
    const plain = name.split(' ').map(singular).join(' ');
    const own = nat.with.find((w) => w.names.some((n) => hasWords(plain, n)));
    const weapon = rule.weapons.find((w) => w.names.some((n) => hasWords(name, n)));
    const threatens = !weapon?.noThreat;
    const gap = weapon?.times !== undefined && !weapon.adjacent && nat.natural > 0 ? { inner: nat.natural } : {};
    if (own)
        return { far: own.reach, ...gap, threatens };
    if (!weapon)
        return { far: nat.natural, threatens };
    if (weapon.reach !== undefined)
        return { far: Math.max(weapon.reach, nat.natural), threatens };
    return { far: nat.natural * weapon.times, ...gap, threatens };
}
export const inBand = (b, distance) => distance <= b.far + 1e-9 && (b.inner === undefined || distance > b.inner + 1e-9);
/** Squares between two tokens, nearest square of one to nearest of the other, height counted. */
export function squaresApart(state, extent, a, b, diagonal) {
    const x = footprintSquares(state.grid, extent, a, a.size);
    const y = footprintSquares(state.grid, extent, b, b.size);
    const dx = Math.max(0, x.i0 - y.i1, y.i0 - x.i1);
    const dy = Math.max(0, x.j0 - y.j1, y.j0 - x.j1);
    return squares3D(dx, dy, heightSquares(elevation(a, state.scene) - elevation(b, state.scene), diagonal), diagonal);
}
/** Squares from one square to a token's footprint, height given. */
export function squaresToFootprint(state, extent, c, t, dz, diagonal) {
    const y = footprintSquares(state.grid, extent, t, t.size);
    return squares3D(Math.max(0, y.i0 - c.i, c.i - y.i1), Math.max(0, y.j0 - c.j, c.j - y.j1), heightSquares(dz, diagonal), diagonal);
}
/** Whether the table measures attacks here: not with free move on, not on a gridless map. */
export const measuresAttacks = (state) => state.scene.freeMove !== true && !isGridless(state.grid);
const inUnit = (n, d) => `${Math.round(n * 100) / 100} ${d.unit}`;
/** What is wrong with a melee attack at this distance (in the unit), in words; null when it reaches. */
export function reachWarning(attack, band, distance, d) {
    if (inBand(band, distance))
        return null;
    if (distance > band.far)
        return `${attack}: the target is ${inUnit(distance, d)} away, past its reach of ${inUnit(band.far, d)}.`;
    return `${attack}: the target is ${inUnit(distance, d)} away, too close: it reaches ${inUnit(band.far, d)} and can't hit at ${inUnit(band.inner, d)} or nearer.`;
}
/** The whole range increments a shot crosses, each's penalty, and its greatest range: the `most` of them. `has`: a feat the shooter carries. */
export function rangeOf(rule, distance, increment, thrown, has = () => false) {
    const feats = rule.feats.filter((f) => has(f.feat));
    const each = feats.reduce((n, f) => n * ((thrown ? f.thrownTimes : f.times) ?? 1), increment);
    return { steps: Math.floor((distance + 1e-9) / each), greatest: each * (thrown ? rule.mostThrown : rule.most), penalty: feats.reduce((p, f) => f.penalty ?? p, rule.penalty) };
}
/** The attacks a charge may end at (`charge.ts`), from what the board knows on both sides: the token's own weapon rows and its size on the map. */
export function chargeBands(rule, o) {
    const nat = reachBySize(rule, null, o.size);
    return (o.values?.weapons ?? []).filter((w) => !w.ranged).map((w) => reachBand(rule, nat, w.name));
}
export const rangeWarning = (attack, distance, greatest, d) => `${attack}: the target is ${inUnit(distance, d)} away, past its greatest range of ${inUnit(greatest, d)}.`;
/** What a warned attack's button says. */
export const ATTACK_ANYWAY = 'Attack anyway';
//# sourceMappingURL=weaponReach.js.map